import { AdvanceBatchStatus } from '@/types';

/**
 * Advance COD batch lifecycle model (DESIGN-dashboard v2.1/v2.2).
 *
 * The merchant-facing lifecycle is a FIXED 5-step sequence; each backend
 * status maps onto it deterministically. `rejected` / `failed` are terminal
 * branches: the node they stop at renders as rejected, everything after it
 * as not-applicable.
 */

export const LIFECYCLE_STEPS = [
  'submitted',
  'approved',
  'disbursed',
  'in_progress',
  'closed',
] as const;

export type LifecycleStepKey = (typeof LIFECYCLE_STEPS)[number];

export type LifecycleNodeState = 'completed' | 'current' | 'pending' | 'rejected' | 'not_applicable';

export interface LifecycleNode {
  key: LifecycleStepKey;
  state: LifecycleNodeState;
  /** true on the `approved` node of a partially_approved batch (amber dot). */
  partial: boolean;
}

interface LifecycleShape {
  /** count of completed steps from the start */
  completed: number;
  /** index of the "current" (live) step, or null */
  current: number | null;
  /** index where the lifecycle terminally stopped (rejected/failed), or null */
  rejectedAt: number | null;
}

const STATUS_SHAPE: Record<AdvanceBatchStatus, LifecycleShape> = {
  draft: { completed: 0, current: 0, rejectedAt: null },
  submitting: { completed: 0, current: 0, rejectedAt: null },
  pending: { completed: 1, current: 1, rejectedAt: null },
  queued: { completed: 1, current: 1, rejectedAt: null },
  approved: { completed: 2, current: 2, rejectedAt: null },
  partially_approved: { completed: 2, current: 2, rejectedAt: null },
  disbursed: { completed: 3, current: 3, rejectedAt: null },
  in_progress: { completed: 3, current: 3, rejectedAt: null },
  closed: { completed: 5, current: null, rejectedAt: null },
  rejected: { completed: 1, current: null, rejectedAt: 1 },
  failed: { completed: 0, current: null, rejectedAt: 0 },
};

export function getLifecycleNodes(status: AdvanceBatchStatus): LifecycleNode[] {
  const shape = STATUS_SHAPE[status] ?? STATUS_SHAPE.draft;
  return LIFECYCLE_STEPS.map((key, index) => {
    let state: LifecycleNodeState;
    if (shape.rejectedAt !== null) {
      state =
        index < shape.rejectedAt
          ? 'completed'
          : index === shape.rejectedAt
            ? 'rejected'
            : 'not_applicable';
    } else if (index < shape.completed) {
      state = 'completed';
    } else if (shape.current !== null && index === shape.current) {
      state = 'current';
    } else {
      state = 'pending';
    }
    return { key, state, partial: status === 'partially_approved' && key === 'approved' };
  });
}

/**
 * Batch status pill palette (DESIGN §3) — RemittancePillBadge styleMap shape,
 * 12%-opacity tints. `closed` is the one NEW token (charcoal @8%: terminal,
 * neither good nor bad). draft/submitting (pre-submit local states) share the
 * neutral grey; failed shares the red terminal token.
 */
export const batchStatusStyleMap: Record<string, { color: string; background: string }> = {
  draft: { color: 'rgb(115,123,140)', background: 'rgba(115,123,140,0.12)' },
  submitting: { color: 'rgb(115,123,140)', background: 'rgba(115,123,140,0.12)' },
  pending: { color: 'rgb(187,120,27)', background: 'rgba(245,158,11,0.12)' },
  queued: { color: 'rgb(115,123,140)', background: 'rgba(115,123,140,0.12)' },
  approved: { color: 'rgb(59,130,246)', background: 'rgba(59,130,246,0.12)' },
  partially_approved: { color: 'rgb(187,120,27)', background: 'rgba(245,158,11,0.12)' },
  rejected: { color: 'rgb(220,40,40)', background: 'rgba(220,40,40,0.12)' },
  disbursed: { color: 'rgb(39,155,101)', background: 'rgba(49,196,127,0.12)' },
  in_progress: { color: 'rgb(124,58,237)', background: 'rgba(124,58,237,0.12)' },
  closed: { color: 'rgb(57,57,60)', background: 'rgba(57,57,60,0.08)' },
  failed: { color: 'rgb(220,40,40)', background: 'rgba(220,40,40,0.12)' },
};

/**
 * Statuses offered as filter chips (DESIGN v2.5 — merchant-meaningful set).
 * draft/submitting never appear (the backend hides them, QA defect 16);
 * `failed` IS offered — merchants see failed submissions with a readable
 * failure reason on the detail page.
 */
export const BATCH_FILTER_STATUSES = [
  'pending',
  'queued',
  'approved',
  'partially_approved',
  'disbursed',
  'in_progress',
  'closed',
  'rejected',
  'failed',
] as const;
